import React, { useState, useEffect, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Check, X } from 'lucide-react'

import CustomCheckbox from '../CustomCheckbox'
import {
  adoptAccountTargetTitle,
  type AccountTargetTitleVariant,
} from '../../lib/accountTargetTitleLibrary'
import { mergeTargetTitleOptions } from '../../utils/targetTitleUtils'

import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

interface TargetTitleEditorProps {
  data: string[]
  onChange: (data: string[]) => void
  onAddTitleRef?: (fn: () => void) => void
  highlightedTitleId?: string | null
  accountTitleVariants?: AccountTargetTitleVariant[]
  accountTitleVariantsLoading?: boolean
}

export default function TargetTitleEditor({
  data,
  onChange,
  onAddTitleRef,
  highlightedTitleId,
  accountTitleVariants = [],
  accountTitleVariantsLoading = false,
}: TargetTitleEditorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [availableTitles, setAvailableTitles] = useState<string[]>([])
  const [editingTitle, setEditingTitle] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [showSearchInput, setShowSearchInput] = useState(false)
  const [librarySectionOpen, setLibrarySectionOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const selectedTitle = Array.isArray(data) && data.length > 0 ? data[0] : null

  const persistSelection = (title: string, titlesPool: string[]) => {
    const others = titlesPool.filter((t) => t !== title)
    onChange([title, ...others])
  }

  // Keep editor list in sync when resume data loads or optimization updates titles
  useEffect(() => {
    const merged = mergeTargetTitleOptions(data)
    if (merged.length > 0) {
      setAvailableTitles(merged)
    }
  }, [data])

  // Function to show search input and focus it
  const handleShowSearchInput = () => {
    setShowSearchInput(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  // Expose handleShowSearchInput to parent via ref
  useEffect(() => {
    if (onAddTitleRef) {
      onAddTitleRef(handleShowSearchInput)
    }
  }, [onAddTitleRef])

  const handleAddTitle = () => {
    const title = searchQuery.trim()
    if (!title) return

    // Add to available titles if not already there
    if (!availableTitles.includes(title)) {
      setAvailableTitles([...availableTitles, title].sort())
    }

    // Select this title (only one can be selected)
    const nextPool = [...availableTitles, title]
    persistSelection(title, nextPool)
    setSearchQuery('')
    setShowSearchInput(true) // Keep search input visible after adding
  }

  const handleSelectTitle = (title: string) => {
    persistSelection(title, availableTitles)
  }

  const handleDeleteTitle = (title: string, e: React.MouseEvent) => {
    e.stopPropagation()
    // Remove from available titles
    const updated = availableTitles.filter((t) => t !== title)
    setAvailableTitles(updated)
    
    // If deleted title was selected, clear selection
    if (selectedTitle === title) {
      onChange([])
    }
    
    // If editing this title, stop editing
    if (editingTitle === title) {
      setEditingTitle(null)
    }
  }

  const handleCopyTitle = (title: string, e: React.MouseEvent) => {
    e.stopPropagation()
    // Create a new title with the same text
    const newTitle = `${title} (copy)`
    const uniqueTitle = availableTitles.includes(newTitle) 
      ? `${newTitle} ${Date.now()}` 
      : newTitle
    
    // Add to available titles
    setAvailableTitles([...availableTitles, uniqueTitle].sort())
    
    // Select the new title
    persistSelection(uniqueTitle, [...availableTitles, uniqueTitle])
    
    // Open for editing
    setEditingTitle(uniqueTitle)
    setEditingValue(title)
  }

  const handleStartEdit = (title: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingTitle(title)
    setEditingValue(title)
  }

  const handleSaveEdit = (oldTitle: string) => {
    const newTitle = editingValue.trim()
    if (!newTitle) {
      // If empty, cancel editing
      setEditingTitle(null)
      return
    }

    // Check if new title already exists (excluding the current one being edited)
    const existingTitle = availableTitles.find(t => t === newTitle && t !== oldTitle)
    
    if (existingTitle) {
      // If title already exists with the same name, remove the duplicate being edited
      const updated = availableTitles.filter(t => t !== oldTitle).sort()
      setAvailableTitles(updated)
      
      // Always select the existing title (not the duplicate being removed)
      persistSelection(existingTitle, updated)
    } else {
      // Update in available titles
      const updated = availableTitles.map((t) => t === oldTitle ? newTitle : t).sort()
      setAvailableTitles(updated)

      // If this title was selected, update selection to new name
      if (selectedTitle === oldTitle) {
        persistSelection(newTitle, updated)
      } else if (selectedTitle) {
        persistSelection(selectedTitle, updated)
      } else {
        onChange(updated)
      }
    }

    setEditingTitle(null)
    setEditingValue('')
  }

  const handleCancelEdit = () => {
    setEditingTitle(null)
    setEditingValue('')
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, oldTitle: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit(oldTitle)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEdit()
    }
  }

  const handleKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTitle()
    }
  }

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const isSearching = normalizedSearchQuery.length > 0

  const titleMatchesSearch = (title: string) =>
    title.toLowerCase().includes(normalizedSearchQuery)

  // Filter local titles based on search query
  const filteredTitles = isSearching
    ? availableTitles.filter(titleMatchesSearch)
    : availableTitles

  const currentTitleSet = useMemo(() => {
    const set = new Set<string>()
    for (const title of mergeTargetTitleOptions(data)) {
      const normalized = title.trim().toLowerCase()
      if (normalized) set.add(normalized)
    }
    return set
  }, [data])

  const libraryVariants = useMemo(
    () =>
      accountTitleVariants.filter(
        (variant) => !currentTitleSet.has(variant.title.trim().toLowerCase()),
      ),
    [accountTitleVariants, currentTitleSet],
  )

  const showLibrarySection = accountTitleVariantsLoading || libraryVariants.length > 0

  const filteredLibraryVariants = useMemo(
    () =>
      isSearching
        ? libraryVariants.filter((variant) => titleMatchesSearch(variant.title))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- titleMatchesSearch derives from searchQuery
    [isSearching, libraryVariants, normalizedSearchQuery],
  )

  const hasSearchResults = filteredTitles.length > 0 || filteredLibraryVariants.length > 0

  const handleAdoptLibraryVariant = (variant: AccountTargetTitleVariant, clearSearch = false) => {
    onChange(adoptAccountTargetTitle(data, variant))
    const merged = adoptAccountTargetTitle(data, variant)
    setAvailableTitles(mergeTargetTitleOptions(merged))
    setShowSearchInput(true)
    if (clearSearch) {
      setSearchQuery('')
    }
  }

  const renderLibraryVariantRow = (variant: AccountTargetTitleVariant, clearSearchOnAdopt: boolean) => (
    <div
      key={variant.key}
      className="group relative flex items-start gap-3 rounded hover:bg-surface-muted p-1"
    >
      <div className="pt-0.5">
        <CustomCheckbox
          checked={false}
          onChange={() => handleAdoptLibraryVariant(variant, clearSearchOnAdopt)}
          size="xs"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-text-muted mb-0.5">{variant.sourceLabel}</div>
        <div className="text-sm text-text-secondary">{variant.title}</div>
      </div>
    </div>
  )

  return (
    <div className="space-y-3">
      {/* Search/Add Input - shown when plus button clicked or if there are titles */}
      {(showSearchInput || availableTitles.length > 0 || libraryVariants.length > 0) && (
        <div className="relative flex-1">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search or add target title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDownSearch}
            onBlur={() => {
              // Hide input if empty and no titles exist
              if (!searchQuery.trim() && availableTitles.length === 0) {
                setShowSearchInput(false)
              }
            }}
            className="w-full px-3 py-2 pr-10 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
          />
          <button
            onClick={handleAddTitle}
            disabled={!searchQuery.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-interactive disabled:text-text-muted disabled:cursor-not-allowed transition-colors"
            title="Add title"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      )}

      {/* Local titles and search hits from other resumes */}
      {availableTitles.length > 0 || isSearching ? (
        <div className="space-y-1 p-2">
          {filteredTitles.map((title, titleIndex) => {
              const isSelected = selectedTitle === title
              const isEditing = editingTitle === title
              return (
                <div
                  key={`${titleIndex}-${title}`}
                  className={`group relative flex items-center gap-2 p-2 rounded cursor-pointer transition-all duration-300 hover:bg-surface-muted ${
                    highlightedTitleId === title ? 'bg-brand-primary-subtle ring-2 ring-interactive-focus-ring ring-opacity-50' : ''
                  }`}
                  data-title-id={title}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Don't start editing if clicking on checkbox or action buttons
                    const target = e.target as HTMLElement
                    // Check if click was directly on checkbox or button
                    const isCheckboxClick = target.closest('[role="checkbox"]')
                    const isButtonClick = target.closest('button')
                    // Allow click on label text, but not on checkbox itself
                    if (isCheckboxClick || isButtonClick) {
                      return
                    }
                    if (!isEditing) {
                      handleStartEdit(title, e)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (isEditing) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      const target = e.target as HTMLElement
                      const isCheckboxClick = target.closest('[role="checkbox"]')
                      const isButtonClick = target.closest('button')
                      if (!isCheckboxClick && !isButtonClick) {
                        handleStartEdit(title, e as unknown as React.MouseEvent)
                      }
                    }
                  }}
                >
                  {isEditing ? (
                    <div
                      className="relative flex-1 min-w-0"
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation()
                          handleEditKeyDown(e, title)
                        }}
                        className="w-full min-w-0 pl-2 pr-14 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                        aria-label="Edit target title"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
                        <button
                          type="button"
                          aria-label="Save"
                          title="Save (Enter)"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSaveEdit(title)
                          }}
                          className="rounded p-1 text-interactive transition-colors hover:bg-interactive-subtle hover:text-interactive focus:outline-none focus-visible:ring-2 focus-visible:ring-interactive-focus-ring"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label="Cancel"
                          title="Cancel (Escape)"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCancelEdit()
                          }}
                          className="rounded p-1 text-text-muted transition-colors hover:bg-surface-muted hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-interactive-focus-ring"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-1 cursor-pointer relative">
                        <div 
                          role="presentation"
                          className="flex items-center gap-2 cursor-pointer" 
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <CustomCheckbox
                            checked={isSelected}
                            onChange={() => handleSelectTitle(title)}
                            size="sm"
                          />
                        </div>
                        <span 
                          className={`text-sm ml-1 flex-1 ${isSelected ? 'text-text-primary font-medium' : 'text-text-secondary'}`}
                          title="Click anywhere on the row to edit"
                        >
                          {title}
                        </span>
                      </div>
                      <div 
                        role="button"
                        tabIndex={0}
                        className="flex items-center gap-1 opacity-0 group-hover:opacity-40 transition-opacity" 
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => handleCopyTitle(title, e)}
                          className="p-1 text-text-muted hover:text-interactive hover:opacity-100 transition-colors"
                          title="Copy title"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDeleteTitle(title, e)}
                          className="p-1 text-text-muted hover:text-danger hover:opacity-100 transition-colors"
                          title="Delete title"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}

          {isSearching &&
            filteredLibraryVariants.map((variant) => renderLibraryVariantRow(variant, true))}

          {isSearching && !hasSearchResults && (
            <div className="text-sm text-text-muted py-2">No matching titles found</div>
          )}
        </div>
      ) : showLibrarySection ? (
        <div className="text-sm text-text-muted py-2">
          No target titles on this resume yet. Pick one from other saved titles below or use the field above.
        </div>
      ) : (
        <ResumeSectionEmptyHint sectionType="target_title" />
      )}

      {!isSearching && showLibrarySection && (
        <div className="mt-2 border-t border-border-default pt-3">
          <button
            type="button"
            onClick={() => setLibrarySectionOpen((open) => !open)}
            aria-expanded={librarySectionOpen}
            className="flex w-full items-center gap-2 text-left text-xs font-medium text-text-muted uppercase tracking-wide hover:text-text-secondary transition-colors"
          >
            <motion.svg
              className="w-4 h-4 text-text-muted shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              animate={{ rotate: librarySectionOpen ? 90 : 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </motion.svg>
            <span className="flex-1">Other saved target titles</span>
            {!librarySectionOpen && !accountTitleVariantsLoading && libraryVariants.length > 0 && (
              <span className="normal-case tracking-normal text-text-muted">
                ({libraryVariants.length})
              </span>
            )}
          </button>
          {librarySectionOpen && (
            <div className="mt-2 space-y-2">
              {accountTitleVariantsLoading ? (
                <div className="text-xs text-text-muted">Loading saved titles…</div>
              ) : libraryVariants.length === 0 ? (
                <div className="text-xs text-text-muted">No other saved titles</div>
              ) : (
                libraryVariants.map((variant) => renderLibraryVariantRow(variant, false))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
